import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Plus, PaperPlaneTilt, X } from 'phosphor-react';
import { theme } from '../../styles/theme';
import { ActionSheet, ActionType } from './ActionSheet';
import { useTranslation } from '../../i18n';

interface InputBarProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  onImageSelect?: (file: File, isFromFoodCapture?: boolean) => void;
  selectedImage?: string | null;
  onImageRemove?: () => void;
  showRotatingPlaceholder?: boolean;
  onActionSelect?: (action: ActionType) => void;
  selectedAgent?: string;
  onAgentChange?: (agent: string) => void;
  hasMessages?: boolean;
  messageCount?: number;
}

export interface InputBarRef {
  focus: () => void;
}

const Container = styled.div`
  position: relative;
  width: 100%;
  padding: ${theme.spacing.md};
  padding-bottom: max(${theme.spacing.md}, env(safe-area-inset-bottom));
`;

const shine = keyframes`
  0% {
    background-position: 0% 0%;
  }
  50% {
    background-position: 100% 100%;
  }
  100% {
    background-position: 0% 0%;
  }
`;

const InputWrapper = styled.div<{ $hasImage: boolean }>`
  position: relative;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 16px;

  [data-theme="light"] & {
    background: #FFFFFF;
    box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.06);
  }
  padding: 18px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-rows: ${({ $hasImage }) => ($hasImage ? 'auto auto auto' : 'auto auto')};
  gap: 10px;
  min-height: ${({ $hasImage }) => ($hasImage ? '180px' : '100px')};
  transition: all ${theme.transitions.fast};

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border-radius: 17px;
    padding: 1px;
    background: linear-gradient(
      90deg,
      #834CC9,
      #9F70DA,
      #FF8BD1,
      #9F70DA,
      #834CC9
    );
    background-size: 200% 200%;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity ${theme.transitions.fast};
    pointer-events: none;
  }

  &:focus-within {
    border-color: transparent;
    box-shadow: 0px 0px 20px rgba(131, 76, 201, 0.15), 0px 0px 15px rgba(0, 0, 0, 0.05);

    &::before {
      opacity: 1;
      animation: ${shine} 5s linear infinite;
    }
  }
`;

const fadeInOut = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-5px);
  }
  15% {
    opacity: 1;
    transform: translateY(0);
  }
  85% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(5px);
  }
`;

const TextArea = styled.textarea`
  grid-column: 1 / -1;
  grid-row: 1;
  background: transparent;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  line-height: 24px;
  resize: none;
  border: none;
  outline: none;
  min-height: 24px;
  max-height: 120px;
  overflow-y: auto;
  padding: 0;
  margin: 0;
  z-index: 2;
  position: relative;

  &::placeholder {
    color: transparent;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PlaceholderText = styled.div<{ $isVisible: boolean; $animated?: boolean }>`
  grid-column: 1 / -1;
  grid-row: 1;
  color: #b0b0b0;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  line-height: 24px;
  pointer-events: none;
  z-index: 1;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  ${({ $animated }) => $animated ? `animation: ${fadeInOut} 5s ease-in-out forwards;` : ''}
`;

const LeftButton = styled.div<{ $hasImage: boolean }>`
  grid-column: 1 / 3;
  grid-row: ${({ $hasImage }) => ($hasImage ? '3' : '2')};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightButton = styled.div<{ $hasImage: boolean }>`
  grid-column: 3;
  grid-row: ${({ $hasImage }) => ($hasImage ? '3' : '2')};
  display: flex;
  align-items: center;
`;

const IconButton = styled.button<{ $variant?: 'default' | 'primary' }>`
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};
  flex-shrink: 0;

  background: ${({ $variant }) =>
    $variant === 'primary' ? 'var(--color-primary)' : '#535353'};
  color: #FFFFFF;

  &:hover:not(:disabled) {
    opacity: 0.8;
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImagePreviewWrapper = styled.div`
  grid-column: 1 / -1;
  grid-row: 2;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-bg-elevated);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  transition: all ${theme.transitions.fast};

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;


// Placeholder examples are now loaded from i18n

export const InputBar = forwardRef<InputBarRef, InputBarProps>(({
  value,
  onChange,
  onSend,
  placeholder,
  disabled = false,
  maxLength = 2000,
  onImageSelect,
  selectedImage,
  onImageRemove,
  showRotatingPlaceholder = false,
  onActionSelect,
  hasMessages = false,
  messageCount = 0,
}, ref) => {
  const { t, tArray } = useTranslation();
  const placeholderExamples = tArray('input.placeholders');
  const defaultPlaceholder = placeholder || t('input.defaultPlaceholder');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [, setIsFocused] = useState(false);
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [currentPlaceholder, setCurrentPlaceholder] = useState(defaultPlaceholder);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [isFromFoodCapture, setIsFromFoodCapture] = useState(false);


  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    },
  }));

  // Rotate placeholder every 5 seconds if showRotatingPlaceholder is true
  useEffect(() => {
    if (!showRotatingPlaceholder) {
      setCurrentPlaceholder(defaultPlaceholder);
      return;
    }

    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prev) => (prev + 1) % placeholderExamples.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [showRotatingPlaceholder, defaultPlaceholder, placeholderExamples.length]);

  // Update current placeholder when index changes
  useEffect(() => {
    if (showRotatingPlaceholder) {
      setCurrentPlaceholder(placeholderExamples[currentPlaceholderIndex]);
    }
  }, [currentPlaceholderIndex, showRotatingPlaceholder, placeholderExamples]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) {
        onSend();
      }
    }
  };

  const handleAttachment = () => {
    setIsActionSheetOpen(true);
  };

  const handleActionSelect = (action: ActionType) => {
    if (action === 'capture-food') {
      // Set flag and open file input for food capture
      setIsFromFoodCapture(true);
      fileInputRef.current?.click();
    } else {
      // Pass other actions to parent
      onActionSelect?.(action);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onImageSelect?.(file, isFromFoodCapture);
      // Reset the flag
      setIsFromFoodCapture(false);
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <ActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        onActionSelect={handleActionSelect}
        mode={hasMessages ? 'in-chat' : 'pre-chat'}
        messageCount={messageCount}
      />

      <Container>
        <InputWrapper $hasImage={!!selectedImage}>
        {showRotatingPlaceholder && !value && (
          <PlaceholderText key={currentPlaceholderIndex} $isVisible={true} $animated>
            {currentPlaceholder}
          </PlaceholderText>
        )}
        {!showRotatingPlaceholder && !value && (
          <PlaceholderText $isVisible={true}>
            {defaultPlaceholder}
          </PlaceholderText>
        )}
        <TextArea
          ref={textareaRef}
          value={value}
          aria-label={defaultPlaceholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true);
            // Scroll input into view after keyboard opens (Android WebView safety net)
            if (/android/i.test(navigator.userAgent)) {
              setTimeout(() => {
                textareaRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }, 400);
            }
          }}
          onBlur={() => setIsFocused(false)}
          placeholder=""
          disabled={disabled}
          maxLength={maxLength}
          rows={1}
        />

        {selectedImage && (
          <ImagePreviewWrapper>
            <ImagePreview>
              <img src={selectedImage} alt="Selected" />
              <RemoveImageButton onClick={onImageRemove} aria-label="Remove image">
                <X size={12} weight="bold" />
              </RemoveImageButton>
            </ImagePreview>
          </ImagePreviewWrapper>
        )}

        <LeftButton $hasImage={!!selectedImage}>
          <IconButton
            onClick={handleAttachment}
            disabled={disabled}
            aria-label="Actions"
          >
            <Plus size={16} weight="bold" />
          </IconButton>

          {/* Agent selector hidden — agent picked via welcome-page cards */}

        </LeftButton>

        <RightButton $hasImage={!!selectedImage}>
          <IconButton
            $variant="primary"
            onClick={onSend}
            disabled={(!value.trim() && !selectedImage) || disabled}
            aria-label="Send message"
          >
            <PaperPlaneTilt size={16} weight="fill" />
          </IconButton>
        </RightButton>

        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />
      </InputWrapper>
    </Container>
    </>
  );
});

InputBar.displayName = 'InputBar';
